import React, { useState } from "react"
import ChatToolsDropdown from "./ConversationHeader/ChatToolsDropdown"
import { Button, Input, Label } from "reactstrap"

import DefaultPhoto from "./../../../assets/images/default.jpg"
import { useParams } from "react-router-dom"

import { toast } from "react-toastify"
import "./DateFilterPanel.css"

function ChatHeader({
  loadingMessages,
  clientName,
  isChatOwner,
  setIsChatOwner,
  isChatArchived,
  isUserShareTeamChat,
  setClientName,
  setSendingTemplateLoading,
  lastSession,
  onSession,
  chatData,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  isDateFilterEnabled,
  setIsDateFilterEnabled,
  onApplyDateFilter,
  onClearDateFilter,
}) {
  const params = useParams()

  const [showDateFilter, setShowDateFilter] = useState(false)

  const notify = () =>
    toast.success("The number has been copied to the clipboard.")

  const handleCopyToClipboard = () => {
    let copyText = chatData?.endUser
      ? chatData?.endUser?.phone
      : chatData?.contactName?.number
      ? chatData?.contactName?.number
      : params.id

    navigator.clipboard
      .writeText(copyText)
      .then(() => {
        notify()
      })
      .catch(error => {
        console.error("Error copying to clipboard:", error)
      })
  }

  // Function to get client number from localStorage openChatsTabs
  const getClientFromOpenTabs = () => {
    try {
      const openChatsTabs = localStorage.getItem("openChatsTabs")
      if (openChatsTabs) {
        const parsedTabs = JSON.parse(openChatsTabs)
        const currentTab = parsedTabs.find(tab => tab._id === params.id)
        if (currentTab && currentTab.client) {
          return currentTab.client
        }
      }
    } catch (error) {
      console.error("Error parsing openChatsTabs from localStorage:", error)
    }
    return null
  }

  // Function to determine what to display as the chat title
  const getChatTitle = () => {
    if (loadingMessages) {
      return (
        <div className="placeholder-glow">
          <span className="placeholder col-6 placeholder-lg rounded"></span>
        </div>
      )
    }

    // If we have chatData with contactName, show client name
    if (chatData?.contactName) {
      return clientName.length > 25
        ? clientName.substring(0, 25) + "..."
        : clientName
    }

    // If we have chatData with endUser, show endUser name
    if (chatData?.endUser) {
      return chatData?.endUser?.name
    }

    // If no chatData, check localStorage for client number
    const clientFromTabs = getClientFromOpenTabs()
    if (clientFromTabs) {
      return clientFromTabs
    }

    // Fallback to "New Chat"
    return "New Chat"
  }

  const formatDateForInput = date => {
    if (!date) return ""
    return new Date(date).toISOString().split("T")[0]
  }

  const handleDateFilterToggle = () => {
    setShowDateFilter(!showDateFilter)
    if (showDateFilter && isDateFilterEnabled) {
      // If closing and filter is enabled, clear it
      onClearDateFilter()
    }
  }

  const handleApplyFilter = () => {
    if (startDate && endDate) {
      if (new Date(startDate) <= new Date(endDate)) {
        onApplyDateFilter()
        setShowDateFilter(false)
      } else {
        toast.error("Start date must be before or equal to end date")
      }
    } else {
      toast.error("Please select both start and end dates")
    }
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center conversation-header">
        <div className="d-flex align-items-center flex-grow-1 user-details">
          <img src={DefaultPhoto} />

          <h2
            className="mb-0 flex-grow-1"
            role="button"
            onClick={handleCopyToClipboard}
            title={
              chatData?.endUser
                ? chatData?.endUser?.phone
                : chatData?.contactName?.number
                ? chatData?.contactName?.number
                : params.id
            }
          >
            {getChatTitle()}
          </h2>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            color={isDateFilterEnabled ? "secondary" : "outline-secondary"}
            size="sm"
            onClick={handleDateFilterToggle}
            className="d-flex align-items-center gap-1"
            title="Filter messages by date range"
          >
            <i className="fas fa-calendar-alt"></i>
            {isDateFilterEnabled && (
              <span className="badge badge-light ms-1">ON</span>
            )}
          </Button>

          <ChatToolsDropdown
            isChatOwner={isChatOwner}
            setIsChatOwner={setIsChatOwner}
            isChatArchived={isChatArchived}
            isUserShareTeamChat={isUserShareTeamChat}
            setClientName={setClientName}
            setSendingTemplateLoading={setSendingTemplateLoading}
            lastSession={lastSession}
            onSession={onSession}
          />
        </div>
      </div>

      {showDateFilter && (
        <div className="date-filter-panel p-3 border-top bg-light">
          <div className="row align-items-end">
            <div className="col-md-4">
              <Label for="startDate" className="form-label">
                Start Date
              </Label>
              <Input
                type="date"
                id="startDate"
                value={formatDateForInput(startDate)}
                onChange={e => setStartDate(e.target.value)}
                max={formatDateForInput(new Date())}
              />
            </div>
            <div className="col-md-4">
              <Label for="endDate" className="form-label">
                End Date
              </Label>
              <Input
                type="date"
                id="endDate"
                value={formatDateForInput(endDate)}
                onChange={e => setEndDate(e.target.value)}
                max={formatDateForInput(new Date())}
                min={formatDateForInput(startDate)}
              />
            </div>

            <div className="col-md-4">
              <div className="d-flex gap-2">
                <Button
                  color="secondary"
                  size="sm"
                  onClick={handleApplyFilter}
                  disabled={!startDate || !endDate}
                >
                  Apply
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  outline
                  onClick={onClearDateFilter}
                  disabled={!isDateFilterEnabled}
                >
                  Clear
                </Button>
                <Button
                  color="light"
                  size="sm"
                  onClick={() => setShowDateFilter(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
          {isDateFilterEnabled && startDate && endDate && (
            <div className="mt-2">
              <small className="text-muted">
                <i className="fas fa-info-circle me-1"></i>
                Showing messages from {new Date(
                  startDate
                ).toLocaleDateString()}{" "}
                to {new Date(endDate).toLocaleDateString()}
              </small>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default ChatHeader
